import React, { useMemo, useState } from 'react';
import Select, { ValueType } from '@atlaskit/select';
import InlineEdit from '@atlaskit/inline-edit';
import { Flex } from '@atlaskit/primitives';
import { isJSMProduct, isStatuspage } from '@atlassian/cssxp-utils-product';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { useOpenClose } from '@css-xp/ppc-custom-hooks';
import { WarningDialog } from '@css-xp/base';
import { PricingPlanType } from '@atlassian/cssxp-typescommon';
import { JSM_PREMIUM_OFFERING_ID, splitAndCapitalize } from '@css-xp/ppc-utils';
import { ProductIdPlanOptionsMap } from '../../types/store-types';
import { LinkedEntitlementsDeletionModal } from './linked-entitlements-deletion-modal';

interface OptionType {
  label: string;
  value: string;
  offeringLevel: number | undefined;
}

export const PlanTypeCell = ({
  offeringId,
  offeringPricingPlanOptions = [],
  setSelectedPricingPlanId,
  isTransferStandardPaid = false,
  offeringName,
  offeringLevel,
  hasSandboxesLinked,
  selectedOrDefaultPriceEligibility,
  enableDesignIteration1Changes,
  isOnUpgradeTrial,
  hasLinkedAddOns,
  productId,
  productName,
  shouldRenderForQuotes = false,
}: {
  offeringId: string;
  offeringName?: string;
  offeringPricingPlanOptions: ProductIdPlanOptionsMap[keyof ProductIdPlanOptionsMap];
  setSelectedPricingPlanId: (_updatedPricingPlanId: string, _updatedOfferingId: string) => void;
  isTransferStandardPaid?: boolean;
  offeringLevel?: number;
  hasSandboxesLinked?: boolean;
  selectedOrDefaultPriceEligibility?: `${PricingPlanType}`;
  enableDesignIteration1Changes?: boolean;
  isOnUpgradeTrial?: boolean;
  hasLinkedAddOns?: boolean;
  productId?: string;
  productName?: string;
  // We do not want validations when rendering for quotes so shouldRenderForQuotes is used for clearing those logics
  shouldRenderForQuotes?: boolean;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showLinkedEntitlementsDeletionModal, setShowLinkedEntitlementsDeletionModal] = useState(false);
  const [isAddOnDeleteConfirmModalOpen, setAddOnDeleteConfirmModalOpen] = useState(false);
  const { isOpen: isModalOpen, open: openModal, close: closeModal } = useOpenClose();

  /** modal has to be shown only once for the cart line item, hence keeping the state */
  const [hasShownLinkedEntitlementsModal, setHasShownLinkedEntitlementsModal] = useState(false);

  /**
   * This is the list of options that will be displayed in the dropdown for selecting the plan.
   * If the transfer is standard paid, then the premium plan should not be displayed.
   * For all other cases, all the plans should be displayed.
   */
  const selectOptions: OptionType[] = useMemo(() => {
    const selectPlanOptions = offeringPricingPlanOptions.map(({ offeringName, offeringId, offeringLevel }) => ({
      label: offeringName,
      value: offeringId,
      offeringLevel: offeringLevel,
    }));
    // for upgrade trial case, we will be showing only one plan for user with no change
    if (!shouldRenderForQuotes && isOnUpgradeTrial) {
      return selectPlanOptions.filter((option) => option.label.toLowerCase() === 'standard');
    }

    /** hiding advanced offering for apps on transfer */
    if (isTransferStandardPaid && !isOnUpgradeTrial) {
      return selectPlanOptions.filter(
        (option) => option.label.toLowerCase() !== 'premium' && option.label.toLowerCase() !== 'advanced'
      );
    }
    return selectPlanOptions;
  }, [offeringPricingPlanOptions]);

  const [selectedOption, setSelectedOption] = React.useState(
    selectOptions.find((option) => option.value === offeringId)
  );

  const [selectedOptionForConfirmCall, setSelectedOptionForConfirmCall] = React.useState<OptionType | undefined>();

  const onConfirm = (_selectedOption: ValueType<OptionType>) => {
    if (!_selectedOption?.value || _selectedOption.value === selectedOption?.value) {
      return;
    }

    const selectedOfferingId = _selectedOption.value;

    const offeringPricingPlanOption = offeringPricingPlanOptions.find(
      ({ offeringId }) => offeringId === selectedOfferingId
    );

    setSelectedPricingPlanId(offeringPricingPlanOption?.pricingPlanId ?? '', selectedOfferingId);
  };

  const isItADowngrade = (initialOfferingLevel: number | undefined, currentOfferingLevel: number | undefined) => {
    if (initialOfferingLevel && currentOfferingLevel) {
      return initialOfferingLevel > currentOfferingLevel;
    }
  };

  const renderPlanTypeCellReadView = ({
    offeringName,
    selectedOrDefaultPriceEligibility,
  }: {
    offeringName: string;
    selectedOrDefaultPriceEligibility: `${PricingPlanType}` | undefined;
  }) => {
    if (!selectedOrDefaultPriceEligibility) {
      return <div>{offeringName}</div>;
    }
    return (
      <div>
        <div>{offeringName}</div>
        <div className="mt-4 product-rows-container--price-eligibility">
          {splitAndCapitalize(selectedOrDefaultPriceEligibility)}
        </div>
      </div>
    );
  };
  const addonsRemoveModalTitle = useMemo(() => {
    const planChangeType = isItADowngrade(offeringLevel, selectedOptionForConfirmCall?.offeringLevel)
      ? 'Downgrading'
      : 'Upgrading';
    return `${planChangeType} will remove add-ons`;
  }, [offeringLevel, selectedOptionForConfirmCall]);

  const renderWarningDialog = () => {
    return (
      <WarningDialog
        isOpen={isAddOnDeleteConfirmModalOpen}
        onClose={() => {
          setIsEditing(false);
          setAddOnDeleteConfirmModalOpen(false);
        }}
        onConfirm={() => {
          setSelectedOption(selectedOptionForConfirmCall);
          setIsEditing(false);
          onConfirm(selectedOptionForConfirmCall);
          setAddOnDeleteConfirmModalOpen(false);
        }}
        header={addonsRemoveModalTitle}
        description={
          <>
            {productId && isJSMProduct(productId) && `${productName} add-ons are only available on the Premium plan`}
            {productId && isStatuspage(productId) && `${productName} add-ons are available based on specific plans`}.
            Moving your subscription to {selectedOptionForConfirmCall?.label} will cancel any linked add-on
            subscriptions. <br />
            <br /> Are you sure you want to continue?
          </>
        }
        okLabel={'Continue'}
        testId={'remove-add-on-line-item-confirm'}
      />
    );
  };

  return (
    <div data-testid="plan-type-cell-edit">
      <InlineEdit<ValueType<OptionType>>
        defaultValue={selectedOption}
        isEditing={isEditing}
        editView={(fieldProps) => {
          return (
            <div>
              <Select
                {...fieldProps}
                options={selectOptions}
                onChange={(selectedOption) => {
                  if (selectedOption) {
                    if (!shouldRenderForQuotes) {
                      if (
                        hasLinkedAddOns &&
                        productId &&
                        ((isJSMProduct(productId) && selectedOption.value !== JSM_PREMIUM_OFFERING_ID) ||
                          isStatuspage(productId))
                      ) {
                        setAddOnDeleteConfirmModalOpen(true);
                        setSelectedOptionForConfirmCall(selectedOption);
                        return;
                      }
                    }

                    setSelectedOption(selectedOption);
                    setIsEditing(false);

                    if (!shouldRenderForQuotes) {
                      if (hasSandboxesLinked && isItADowngrade(offeringLevel, selectedOption?.offeringLevel)) {
                        openModal();
                        setShowLinkedEntitlementsDeletionModal(true);
                      }
                    }

                    // calling onConfirm here manually because the component is stateless now
                    // and we have hidden the action buttons as well.
                    onConfirm(selectedOption);
                  }
                }}
                /*
                /* Portal target is used because the dropdown was getting hidden inside the custom panel
                 */
                menuPortalTarget={document.body}
                autoFocus
                openMenuOnFocus
              />
            </div>
          );
        }}
        readView={() => (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setIsEditing(true);
            }}
          >
            {!selectedOption ? (
              offeringName ? (
                <Flex alignItems="center">
                  {renderPlanTypeCellReadView({ offeringName, selectedOrDefaultPriceEligibility })}{' '}
                  {enableDesignIteration1Changes && <ChevronDownIcon label="More" size="medium" />}
                </Flex>
              ) : (
                <div>--</div>
              )
            ) : (
              <Flex alignItems="center">
                {renderPlanTypeCellReadView({ offeringName: selectedOption.label, selectedOrDefaultPriceEligibility })}{' '}
                {enableDesignIteration1Changes && <ChevronDownIcon label="More" size="medium" />}
              </Flex>
            )}
          </div>
        )}
        onConfirm={onConfirm}
        hideActionButtons
      />

      {showLinkedEntitlementsDeletionModal && (
        <LinkedEntitlementsDeletionModal
          isOpen={isModalOpen}
          onClose={() => {
            closeModal();
            setShowLinkedEntitlementsDeletionModal(false);
            setHasShownLinkedEntitlementsModal(!hasShownLinkedEntitlementsModal);
          }}
          initialOfferingName={offeringName}
          newOfferingName={selectedOption?.label}
          hasShownLinkedEntitlementsModal={hasShownLinkedEntitlementsModal}
        />
      )}

      {renderWarningDialog()}
    </div>
  );
};
